import {
  Flex,
  Box,
  Text,
  HStack,
  Tooltip,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowDownToBracket,
  faCalendarDay,
  faFileLines,
  faTriangleExclamation,
  faInfoCircle,
  faBookSparkles,
  faEnvelope,
  faUser,
  faCopy
} from '@fortawesome/pro-regular-svg-icons';
import {
  Badge,
  ButtonIcon,
  ButtonText,
  Link,
  Modal
} from '@morressier/alembic';
import { formatDate, timeFormatDeadlineDate } from 'src/utils/date';
import {
  getPeerReviewModelForWorkflow,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { downloadFile } from 'src/utils/file';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import TextWithIcon from 'src/components/TextWithIcon';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { ContextualReviewBadge } from 'src/components/Review/Badges';
import { useMemo, useState } from 'react';
import { RequestExtensionModal } from 'src/components/Review/RequestExtensionModal';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { RATING_OPTIONS, SCORE_MAP } from 'src/utils/reviews';
import { REVIEW_STATUS_MAP } from 'src/constants';
import ContactJournal from '../../components/ContactPublisher/ContactPublisher';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import { formatAuthorFull, formatAuthorLocation } from 'src/utils/formatAuthor';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { DividerVertical } from '../../components/Submission/OverviewTab';

interface ArticleHeaderProps {
  onClose: () => void;
}
const MAX_VISIBLE = 1;

const ReviewJournalHeader = ({ onClose }: ArticleHeaderProps) => {
  const toast = useToast();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const modalWidth = useBreakpointValue({ base: '95vw', md: '720px' });
  const {
    review,
    submissionContext,
    workflowResponse: { workflowData, manuscriptId },
    fieldsResponse,
    hasSubmittedReviewInCurrentRound,
    availableActions,
    submissionId
  } = useReviewData();
  const peerReviewModel = getPeerReviewModelForWorkflow(
    submissionContext?.config,
    fieldsResponse?.articleWorkflowId
  );
  const submissionType = fieldsResponse.articleTypeName;
  const title = fieldsResponse.fields.Title!.value as string;
  const submissionDate = workflowData.states?.find(state =>
    ['initial', 'transferOffered'].includes(state.name)
  )?.completedWith?.date;
  const enableConsolidatedPdf =
    submissionContext?.config?.enableConsolidatedPdf;
  const manuscriptUrl = enableConsolidatedPdf
    ? (
        fieldsResponse?.fields?.AnonymizedConsolidatedPdf
          ?.value as ISubmissionFile
      )?.url
    : (fieldsResponse?.fields?.Manuscript?.value as ISubmissionFile)?.url;

  const titlePageUrl = (
    fieldsResponse?.fields?.TitlePage?.value as ISubmissionFile
  )?.url;

  // Authors Info
  const [showAll, setShowAll] = useState(false);
  const { getFieldState } = useSubmissionFields('review');
  const Authors = getFieldState<FormFieldAuthors>('Authors');
  const authors = useMemo(
    () => (Array.isArray(Authors?.value) ? Authors.value : []),
    [Authors]
  );
  const handleCopy = async (variable: string) => {
    await navigator.clipboard.writeText(variable);
    toast({
      variant: 'success',
      title: 'Copied to clipboard',
      description: '',
      isClosable: true
    });
  };
  const remainingCount = authors.length - MAX_VISIBLE;
  const shouldShowToggle = authors.length > MAX_VISIBLE;
  const reviewCurrentState = review.workflow.currentState;
  const showDeadlineForState =
    reviewCurrentState === 'rescinded' || !hasSubmittedReviewInCurrentRound();

  const reviewDeadline = review.workflow.currentDeadline;

  const [isExtensionRequestedModal, setIsExtensionRequestedModal] =
    useState<boolean>(false);
  const deadlineRequested =
    review?.workflow.deadlineExtensions?.status === 'requested';
  const proposedDate = deadlineRequested
    ? review?.workflow.deadlineExtensions?.proposedDate
    : null;
  const daysToDeadline = calculateDaysToDeadline(
    new Date(reviewDeadline ?? '')
  );
  const deadlineOverdue = daysToDeadline <= 0;

  const reviewScore = review.caseData.firstAssessment?.reviewScore;
  const reviewerRating = submissionContext?.config?.reviewerRating;
  let scoreHeading: string | null = null;
  if (
    reviewerRating &&
    REVIEW_STATUS_MAP[reviewCurrentState]?.label !== 'In Progress'
  ) {
    scoreHeading = 'Review Not Yet Scored';
    if (reviewScore) {
      const ratingOption = Object.values(RATING_OPTIONS).find(
        option => option.label.toLowerCase() === reviewScore.score
      );
      const scoreNumber = Object.values(SCORE_MAP).find(
        option => option.valueNumber === ratingOption?.value
      );
      scoreHeading = `Review Scored as ${scoreNumber?.text}`;
    }
  }
  const selectedFocusIssue = submissionContext?.data?.focusIssues?.find(
    item => item.id === fieldsResponse?.fields?.FocusIssue?.value
  );
  return (
    <>
      <Box p={4} bg="white" border="1px solid" borderColor="gray.200">
        <Flex width={'100%'} flexDirection={'column'} gap={2}>
          <Flex direction={'column'} minWidth={0} overflow="hidden">
            <HStack spacing={4} mb={2}>
              <Box borderRight={'1px solid'} borderColor="gray.200">
                <ButtonText onClick={onClose}>
                  <FontAwesomeIcon icon={faArrowLeft} size="lg" /> Back
                </ButtonText>
              </Box>
              <Text as="span" textStyle={'headingItem'} fontWeight="semibold">
                # {manuscriptId}
              </Text>

              {/* Contact Publisher */}
              <Flex alignItems={'center'}>
                <DividerVertical />
                <Text textStyle={'headingItem'}>Contact Journal</Text>
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top-start"
                  label={'Contact Journal'}
                  data-cy="info-tooltip"
                >
                  <ButtonIcon
                    size="regular"
                    ariaLabel="Send Mail"
                    variant="primary"
                    onClick={() => setIsContactModalOpen(true)}
                    icon={<FontAwesomeIcon icon={faEnvelope} size="lg" />}
                  />
                </Tooltip>
              </Flex>
            </HStack>

            <Box textStyle="headingItem" mb={2} noOfLines={1}>
              <RichTextPreview value={title} />
            </Box>

            <Flex wrap="wrap" align="center" textStyle={'textContext'}>
              <HStack spacing={1} mr={3}>
                <TextWithIcon
                  icon={faCalendarDay}
                  text={formatDate(new Date(submissionDate ?? ''))}
                />
              </HStack>
              <HStack spacing={1} mr={3}>
                <TextWithIcon icon={faFileLines} text={submissionType} />
              </HStack>
              {selectedFocusIssue && (
                <HStack spacing={1} mr={3}>
                  <Flex flexDirection="row" alignContent="center" gap={2}>
                    <TextWithIcon
                      icon={faBookSparkles}
                      text={'Special Issue: '}
                    />
                    <Tooltip
                      label={selectedFocusIssue.name}
                      hasArrow
                      placement="top"
                    >
                      <Box maxW="220px" minW="0">
                        {selectedFocusIssue?.issueLink ||
                        submissionContext?.data?.guidelineLink ? (
                          <Link
                            variant="primary"
                            href={
                              selectedFocusIssue?.issueLink ||
                              submissionContext?.data?.guidelineLink
                            }
                            target="_blank"
                            style={{ display: 'block' }}
                          >
                            <Text textStyle="textContext" isTruncated>
                              {selectedFocusIssue.name}
                            </Text>
                          </Link>
                        ) : (
                          <Text textStyle="textContext" isTruncated>
                            {selectedFocusIssue.name}
                          </Text>
                        )}
                      </Box>
                    </Tooltip>
                  </Flex>
                </HStack>
              )}
              {manuscriptUrl && (
                <HStack spacing={1} mr={3}>
                  <ButtonText
                    leftIcon={
                      <FontAwesomeIcon
                        icon={faArrowDownToBracket}
                      ></FontAwesomeIcon>
                    }
                    fontSize={'sm'}
                    onClick={() => {
                      downloadFile(manuscriptUrl);
                    }}
                  >
                    Manuscript
                  </ButtonText>
                </HStack>
              )}{' '}
              {titlePageUrl && !enableConsolidatedPdf && (
                <HStack spacing={1} marginLeft={2}>
                  <ButtonText
                    leftIcon={
                      <FontAwesomeIcon
                        icon={faArrowDownToBracket}
                      ></FontAwesomeIcon>
                    }
                    fontSize={'sm'}
                    onClick={() => {
                      downloadFile(titlePageUrl);
                    }}
                  >
                    Title page
                  </ButtonText>
                </HStack>
              )}
            </Flex>
            {/* Authors Info */}
            {peerReviewModel !== 'doubleAnonymous' && (
              <Flex
                wrap="wrap"
                columnGap={1}
                alignItems="center"
                width="100%"
                minWidth={0}
              >
                {authors.length >= 1 && (
                  <Flex align="center" minWidth={0} overflow="hidden">
                    <Box flexShrink={0}>
                      <TextWithIcon icon={faUser} />
                    </Box>
                    <Tooltip
                      label={formatAuthorFull(authors[0]!)}
                      isDisabled={!formatAuthorLocation(authors[0]!)}
                      hasArrow
                      placement="top"
                    >
                      <Text isTruncated textStyle="textContext" minWidth={0}>
                        <Text as="span" fontWeight="500">
                          {`${authors[0]?.firstName} ${authors[0]?.lastName}`}
                        </Text>
                        {formatAuthorLocation(authors[0]!)
                          ? `, ${formatAuthorLocation(authors[0]!)}`
                          : ''}
                      </Text>
                    </Tooltip>
                    <Box flexShrink={0}>
                      <ButtonIcon
                        variant="primary"
                        ariaLabel="copytoclipboard"
                        icon={<FontAwesomeIcon icon={faCopy} />}
                        size="small"
                        onClick={() =>
                          handleCopy(formatAuthorFull(authors[0]!))
                        }
                      />
                    </Box>
                  </Flex>
                )}

                {shouldShowToggle && (
                  <ButtonText
                    variant="outline"
                    fontSize={'sm'}
                    onClick={() => setShowAll(prev => !prev)}
                  >
                    {showAll
                      ? 'Show Less'
                      : `+${remainingCount} author${
                          remainingCount > 1 ? 's' : ''
                        }`}
                  </ButtonText>
                )}
              </Flex>
            )}
            <Flex wrap="wrap" align="center" textStyle={'textContext'}>
              {showDeadlineForState && reviewDeadline && (
                <HStack spacing={1}>
                  <Text>Deadline:</Text>
                  <Text
                    textColor={
                      daysToDeadline < 1 ? 'errorFillInverse' : 'black'
                    }
                  >
                    {timeFormatDeadlineDate(reviewDeadline)}
                  </Text>
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label={
                      <Text textStyle="textContext">
                        {review?.workflow.deadlineExtensions?.status ===
                          'denied' &&
                          'The editors have rejected your last extension request.'}
                        {deadlineOverdue
                          ? `Your review is overdue by ${Math.abs(
                              daysToDeadline
                            )} days.`
                          : `You have ${daysToDeadline} days to complete your assessment.`}
                      </Text>
                    }
                    borderRadius={4}
                    data-cy="info-tooltip"
                  >
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      width={6}
                      height={6}
                      backgroundColor={
                        daysToDeadline < 1
                          ? 'errorFillInverse'
                          : 'attentionSecondaryFill'
                      }
                      marginRight={2}
                      color={
                        daysToDeadline < 1
                          ? 'neutralPrimaryContentInverse'
                          : 'successPrimaryFill'
                      }
                      borderRadius="small"
                    >
                      <FontAwesomeIcon
                        icon={
                          daysToDeadline < 1
                            ? faTriangleExclamation
                            : faInfoCircle
                        }
                      />
                    </Flex>
                  </Tooltip>
                </HStack>
              )}

              <HStack>
                {deadlineRequested ? (
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label={`Extension requested until ${
                      proposedDate ? formatDate(new Date(proposedDate)) : ''
                    }`}
                    borderRadius={4}
                    data-cy="extension-tooltip"
                  >
                    <Badge size="context">
                      <Text textStyle="textContext">Extension requested</Text>
                    </Badge>
                  </Tooltip>
                ) : (
                  <>
                    {!deadlineRequested &&
                      (reviewCurrentState === 'rescinded' ||
                        !hasSubmittedReviewInCurrentRound()) &&
                      availableActions.contains(
                        'RequestReviewDeadlineExtension'
                      ) && (
                        <ButtonText
                          onClick={() => setIsExtensionRequestedModal(true)}
                          fontSize={'sm'}
                        >
                          Request Extension
                        </ButtonText>
                      )}{' '}
                  </>
                )}
              </HStack>
              <HStack spacing={2}>
                {reviewDeadline && <Text color="gray.200">|</Text>}

                <ContextualReviewBadge
                  reviewState={reviewCurrentState}
                  context="reviewer"
                />
              </HStack>
              {scoreHeading && (
                <HStack spacing={2} gap={0}>
                  <Text color="gray.200" mx={2}>
                    |
                  </Text>

                  <Text textStyle="headingItem">{scoreHeading}</Text>
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement={
                      (reviewScore?.justification?.length ?? 0) > 100
                        ? 'auto'
                        : 'top'
                    }
                    label={`${
                      reviewScore?.justification
                        ? reviewScore.justification
                        : 'Your Review Score will be available upon manuscript decision'
                    }`}
                    borderRadius={4}
                    data-cy="info-tooltip"
                  >
                    <ButtonIcon
                      size="small"
                      ariaLabel="field information"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      data-cy="info-icon"
                    />
                  </Tooltip>
                </HStack>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>

      <RequestExtensionModal
        isOpen={isExtensionRequestedModal}
        onClose={() => setIsExtensionRequestedModal(false)}
        remainingDeadlineAutoExtension={
          review?.workflow.deadlineExtensions?.remainingAutoextensions
        }
      />
      {manuscriptId && (
        <Modal
          width={modalWidth}
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        >
          <ContactJournal
            submissionId={submissionId}
            submissionContextId={submissionContext?.id}
            manuscriptId={manuscriptId}
            setIsContactModalOpen={setIsContactModalOpen}
            reviewRequestId={review?.id}
          />
        </Modal>
      )}
      {/* Author Info Modal */}
      <Modal isOpen={showAll} width="720px" onClose={() => setShowAll(false)}>
        <Flex padding={4} flexDirection="column" gap={4}>
          <Text textStyle="headingGroup">Authors</Text>
          <Flex flexDirection="column">
            {authors.map((author, idx) => (
              <Flex key={idx} align="center" gap={1}>
                <Tooltip
                  label={formatAuthorFull(author)}
                  isDisabled={!formatAuthorLocation(author)}
                  hasArrow
                  placement="top"
                >
                  <Text isTruncated textStyle="textContext">
                    <Text as="span" fontWeight="500">
                      {`${author.firstName} ${author.lastName}`}
                    </Text>
                    {formatAuthorLocation(author)
                      ? `, ${formatAuthorLocation(author)}`
                      : ''}
                  </Text>
                </Tooltip>
                <ButtonIcon
                  variant="primary"
                  ariaLabel="copytoclipboard"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  size="small"
                  onClick={() => handleCopy(formatAuthorFull(author))}
                />
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default ReviewJournalHeader;
